import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation } from '@apollo/client/react';

import { createRequiredContext } from 'src/context/createRequiredContext';
import { AppRequestDeclineDocument } from 'src/graphql/actions/appRequests/appRequestDecline.mutation';
import { AppRequestAcceptDocument } from 'src/graphql/actions/appRequests/appRequestAccept.mutation';
import { ClientError } from 'src/utils/errors';
import { refetchAppProtections } from 'src/containers/Applications/Protections/api';

import { refetchAppRequests } from '../api';

import { VendorRequestsFilter } from './types';

const FILTER_PARAM = 'filter';
const DEFAULT_FILTER: VendorRequestsFilter = 'received';

const VENDOR_REQUESTS_FILTERS: VendorRequestsFilter[] = ['received', 'sent', 'declined'];

const isVendorRequestsFilter = (value: string | null): value is VendorRequestsFilter =>
  VENDOR_REQUESTS_FILTERS.includes(value as VendorRequestsFilter);

export type VendorRequestsContextValue = {
  filter: VendorRequestsFilter;
  setFilter: (filter: VendorRequestsFilter) => void;
  page: number;
  setPage: (page: number) => void;
  declineRequest: (id: string, reason: string) => Promise<void>;
  linkRequest: (id: string, applicationId: string) => Promise<void>;
};

const [useVendorRequests, VendorRequestsProvider] = createRequiredContext<VendorRequestsContextValue>();

const VendorRequestsContextProvider = ({ children }: React.PropsWithChildren) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const filterParam = searchParams.get(FILTER_PARAM);
  const filter = isVendorRequestsFilter(filterParam) ? filterParam : DEFAULT_FILTER;

  const handleSetFilter = (nextFilter: VendorRequestsFilter) => {
    setSearchParams((prevSearchParams) => {
      const nextSearchParams = new URLSearchParams(prevSearchParams);
      nextSearchParams.set(FILTER_PARAM, nextFilter);
      return nextSearchParams;
    });
    setPage(1);
  };

  const [declineMutation] = useMutation(AppRequestDeclineDocument);

  const declineRequest = async (id: string, reason: string) => {
    const { data: result } = await declineMutation({ variables: { appRequestId: id, reason } });

    if (!result?.appRequestDecline?.success) throw new ClientError(result?.appRequestDecline?.errors?.[0]);

    refetchAppRequests();
  };

  const [acceptMutation] = useMutation(AppRequestAcceptDocument);

  const linkRequest = async (id: string, applicationId: string) => {
    const { data: result } = await acceptMutation({ variables: { appId: applicationId, appRequestId: id } });

    if (!result?.appRequestAccept?.success) throw new ClientError(result?.appRequestAccept?.errors?.[0]);

    refetchAppRequests();
    refetchAppProtections();
    navigate(`/applications/${applicationId}`);
  };

  const value: VendorRequestsContextValue = {
    filter,
    setFilter: handleSetFilter,
    page,
    setPage,
    declineRequest,
    linkRequest,
  };

  return <VendorRequestsProvider value={value}>{children}</VendorRequestsProvider>;
};

export { useVendorRequests, VendorRequestsContextProvider };
